import { useState, useEffect } from 'react'
import {
  onAuthStateChanged,
  type User,
  type IdTokenResult,
} from 'firebase/auth'
import { auth } from '../firebase'
import { consumeImpersonationTicket, redeemImpersonationTicket } from '../auth/impersonationHandoff'

export type UserRole = 'superAdmin' | 'clientAdmin' | null

export interface AuthClaims {
  role: UserRole
  clientId: string | null
  /** A superAdmin impersonation session opened for REVIEW: reads only (CC review #24). */
  reviewOnly?: boolean
}

export interface AuthState {
  user: User | null
  claims: AuthClaims
  loading: boolean
  error: Error | null
}

/**
 * Base auth hook — subscribes to Firebase onAuthStateChanged and exposes
 * the current user along with their custom claims (role, clientId).
 *
 * Claims are loaded via getIdTokenResult(). The token is force-refreshed
 * once per session to ensure fresh claim data after role changes.
 */
export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({
    user: null,
    claims: { role: null, clientId: null },
    loading: true,
    error: null,
  })

  useEffect(() => {
    // ⚠️ THE TICKET IS TAKEN OUT OF THE URL SYNCHRONOUSLY, BEFORE THE LISTENER.
    // `onAuthStateChanged` fires almost immediately with `null`, and if that is
    // allowed to settle we render a sign-in screen at an operator who just
    // clicked "Login As Client". `handoff` holds `loading` true until the trade
    // finishes. [[feedback_the_state_was_wrong_not_the_wording]]
    const ticket = consumeImpersonationTicket()
    let handoffPending = !!ticket

    if (ticket) {
      redeemImpersonationTicket(ticket)
        .then((r) => {
          handoffPending = false
          // On success `signInWithCustomToken` re-fires the listener below with
          // a real user, so there is nothing to set here.
          if (!r.ok) {
            setState({
              user: null,
              claims: { role: null, clientId: null },
              loading: false,
              error: new Error(r.error),
            })
          }
        })
        .catch(() => { handoffPending = false })
    }

    const unsubscribe = onAuthStateChanged(
      auth,
      async (user) => {
        if (!user) {
          // Do not conclude "signed out" while a hand-off is still in flight.
          if (handoffPending) return
          setState({ user: null, claims: { role: null, clientId: null }, loading: false, error: null })
          return
        }

        try {
          // First read the cached token (fast path for returning sessions).
          let tokenResult: IdTokenResult = await user.getIdTokenResult(/* forceRefresh */ false)
          let role = (tokenResult.claims['role'] as UserRole) ?? null
          // If no role is set in the cached token, the user may have just
          // signed in for the first time — the onClientCreate trigger sets
          // the claim AFTER sign-in completes, so the just-minted token
          // doesn't have it yet. Force-refresh once to pick it up.
          if (!role) {
            tokenResult = await user.getIdTokenResult(/* forceRefresh */ true)
            role = (tokenResult.claims['role'] as UserRole) ?? null
          }
          const claims: AuthClaims = {
            role,
            clientId: (tokenResult.claims['clientId'] as string) ?? null,
            reviewOnly: tokenResult.claims['reviewOnly'] === true,
          }
          setState({ user, claims, loading: false, error: null })
        } catch (err) {
          setState({
            user: null,
            claims: { role: null, clientId: null },
            loading: false,
            error: err instanceof Error ? err : new Error('Failed to load auth claims'),
          })
        }
      },
      (error) => {
        setState({ user: null, claims: { role: null, clientId: null }, loading: false, error })
      }
    )

    return unsubscribe
  }, [])

  return state
}
